/**
 * Identity-first Workshop form catalog.
 *
 * Every id below names a distinct object with its own function and silhouette.
 * Rarity, material, size, and finish are metadata/variants; they never create
 * fake forms such as "painted chair" or "ancient chair".
 */

import type {
  Form,
  FormFamily,
  FormIdentity,
  FormUse,
  IdentitySilhouette,
  ItemRarity,
} from './forms';

interface IdentityGroup {
  readonly key: string;
  readonly family: FormFamily;
  readonly silhouette: IdentitySilhouette;
  readonly proportions: FormIdentity['proportions'];
  readonly cue: string;
  readonly accepts: Form['accepts'];
  readonly use: FormUse;
  readonly tier: Form['tier'];
  readonly ids: readonly string[];
}

const GROUPS = [
  group(
    'masonry-parts',
    'component',
    'slab',
    'low-wide',
    'load-bearing cut-stone edges',
    ['stone'],
    'component',
    2,
    [
      'foundation-slab',
      'cornerstone',
      'capstone',
      'keystone',
      'sill-stone',
      'threshold-stone',
      'coping-stone',
      'stepping-slab',
      'drainage-stone',
      'hearth-slab',
    ],
  ),
  group(
    'timber-parts',
    'component',
    'beam',
    'long-thin',
    'structural grain and load direction',
    ['wood'],
    'component',
    2,
    [
      'joist',
      'rafter',
      'roof-truss',
      'knee-brace',
      'corbel',
      'lintel',
      'sill-beam',
      'crossbeam',
      'trestle-frame',
      'ladder-rung',
    ],
  ),
  group(
    'open-frames',
    'component',
    'frame',
    'upright',
    'an unmistakable framed opening',
    ['wood', 'stone'],
    'component',
    2,
    [
      'window-frame',
      'door-jamb',
      'mullion',
      'transom-bar',
      'shutter-frame',
      'lattice-frame',
      'screen-frame',
      'hatch-frame',
      'vent-frame',
      'skylight-frame',
    ],
  ),
  group(
    'joinery',
    'component',
    'joint',
    'compact',
    'interlocking faces that explain assembly',
    ['wood', 'stone', 'found'],
    'component',
    1,
    [
      'dowel',
      'peg',
      'wedge',
      'shim',
      'mortise-block',
      'tenon-block',
      'scarf-joint',
      'lap-joint',
      'hinge-leaf',
      'latch-plate',
    ],
  ),
  group(
    'mechanisms',
    'component',
    'mechanism',
    'compact',
    'visible rotational or reciprocating motion',
    ['wood', 'stone', 'found'],
    'component',
    3,
    [
      'axle',
      'pulley',
      'sprocket',
      'hand-crank',
      'flywheel',
      'gear-train',
      'ratchet',
      'spring-coil',
      'counterweight',
      'bearing-block',
    ],
  ),
  group(
    'conduits',
    'component',
    'conduit',
    'long-thin',
    'a clear inlet-to-outlet path',
    ['stone', 'found'],
    'component',
    2,
    [
      'pipe',
      'pipe-elbow',
      'pipe-tee',
      'valve-body',
      'spout',
      'nozzle',
      'funnel',
      'chute',
      'duct',
      'vent-cover',
    ],
  ),
  group(
    'surface-parts',
    'component',
    'panel',
    'flat',
    'surface rhythm and edge treatment',
    ['wood', 'stone', 'plant'],
    'component',
    1,
    [
      'floorboard',
      'wallboard',
      'roof-shingle',
      'clay-tile',
      'mosaic-tile',
      'woven-panel',
      'reed-screen',
      'felt-pad',
      'cork-sheet',
      'pegboard',
    ],
  ),
  group(
    'bindings',
    'component',
    'binding',
    'long-thin',
    'loops, tension, and fastening points',
    ['wood', 'plant', 'found'],
    'component',
    1,
    [
      'rope-ladder',
      'chain-link',
      'cable-loop',
      'carry-strap',
      'buckle',
      'clasp',
      'utility-hook',
      'eyelet',
      'spool',
      'reel',
    ],
  ),

  group(
    'compact-seating',
    'furnishing',
    'seat',
    'compact',
    'a distinct sitting posture',
    ['wood', 'stone', 'plant'],
    'place',
    1,
    [
      'rocking-chair',
      'kneeling-chair',
      'deck-chair',
      'sling-chair',
      'saddle-stool',
      'tripod-stool',
      'window-seat',
      'reading-chair',
      'floor-cushion',
      'meditation-seat',
    ],
  ),
  group(
    'shared-seating',
    'furnishing',
    'seat-wide',
    'low-wide',
    'space for a particular shared rest ritual',
    ['wood', 'stone', 'plant'],
    'place',
    2,
    [
      'porch-swing',
      'hanging-seat',
      'conversation-seat',
      'corner-seat',
      'storage-bench',
      'step-stool',
      'drafting-stool',
      'piano-stool',
      'garden-seat',
      'hearth-seat',
      'day-couch',
      'chaise',
      'settle',
      'ottoman',
      'footrest',
    ],
  ),
  group(
    'work-surfaces',
    'furnishing',
    'table',
    'low-wide',
    'a purpose-specific work surface and support layout',
    ['wood', 'stone'],
    'place',
    2,
    [
      'writing-desk',
      'rolltop-desk',
      'drafting-table',
      'tea-table',
      'side-table',
      'console-table',
      'nesting-table',
      'worktable',
      'potting-table',
      'wash-table',
      'breakfast-bar',
      'island-counter',
      'folding-counter',
      'map-table',
      'game-table',
      'display-plinth',
      'plant-stand',
      'lectern',
      'tray-stand',
      'sewing-table',
    ],
  ),
  group(
    'storage-furniture',
    'furnishing',
    'storage',
    'upright',
    'doors, drawers, or open storage that reveal use',
    ['wood', 'stone', 'plant'],
    'place',
    2,
    [
      'wardrobe',
      'armoire',
      'dresser',
      'nightstand',
      'sideboard',
      'hutch',
      'pantry-cupboard',
      'linen-chest',
      'blanket-box',
      'tool-chest',
      'apothecary-cabinet',
      'map-cabinet',
      'specimen-cabinet',
      'shoe-rack',
      'coat-rack',
      'umbrella-stand',
      'wall-shelf',
      'corner-shelf',
      'ladder-shelf',
      'cubby-unit',
      'drawer-unit',
      'crate-stack',
      'bottle-rack',
      'plate-rack',
      'basket-tower',
    ],
  ),
  group(
    'lighting',
    'furnishing',
    'light',
    'upright',
    'a believable light source, shade, and support',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'floor-lamp',
      'desk-lamp',
      'wall-sconce',
      'pendant-light',
      'oil-lamp',
      'candle-stand',
      'candelabrum',
      'paper-lantern',
      'storm-lantern',
      'jar-lantern',
      'mushroom-lamp',
      'reading-light',
      'task-light',
      'path-light',
      'bollard-light',
      'string-light',
      'beacon-lamp',
      'reflector-lamp',
      'hearth-lamp',
      'sun-catcher',
    ],
  ),
  group(
    'garden-furniture',
    'furnishing',
    'garden',
    'upright',
    'plant, water, or habitat function visible at icon size',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'potting-bench',
      'raised-bed',
      'window-box',
      'hanging-planter',
      'herb-rack',
      'seed-tray',
      'compost-bin',
      'rain-barrel',
      'watering-station',
      'hose-reel',
      'garden-cart',
      'wheelbarrow',
      'cold-cloche',
      'propagation-shelf',
      'bee-waterer',
      'butterfly-feeder',
      'bird-feeder',
      'pond-basin',
      'fountain-bowl',
      'plant-ladder',
    ],
  ),
  group(
    'soft-furnishings',
    'furnishing',
    'textile',
    'flat',
    'folds, suspension, and a specific soft use',
    ['plant', 'wood'],
    'place',
    1,
    [
      'quilt-rack',
      'towel-rack',
      'runner-rug',
      'braided-rug',
      'patchwork-rug',
      'wall-tapestry',
      'privacy-curtain',
      'canopy-curtain',
      'hammock',
      'sleeping-roll',
      'pet-blanket',
      'bolster',
      'floor-pillow',
      'window-curtain',
      'picnic-blanket',
    ],
  ),
  group(
    'home-utility',
    'furnishing',
    'utility',
    'upright',
    'a domestic task readable from holders and working height',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'wash-basin',
      'mirror-stand',
      'vanity',
      'boot-scraper',
      'mud-tray',
      'key-board',
      'message-board',
      'calendar-board',
      'chalkboard',
      'firewood-rack',
      'log-basket',
      'magazine-rack',
      'book-cart',
      'laundry-hamper',
      'repair-stand',
    ],
  ),
  group(
    'kitchen-furniture',
    'furnishing',
    'kitchen',
    'upright',
    'food storage or preparation geometry without loose clutter',
    ['wood', 'stone', 'plant'],
    'place',
    2,
    [
      'baker-rack',
      'spice-rack',
      'dish-hutch',
      'crock-stand',
      'bread-box',
      'grain-bin',
      'flour-chest',
      'utensil-crock',
      'drying-cabinet',
      'sink-counter',
      'prep-counter',
      'kneading-board',
      'chopping-block',
      'cooling-rack',
      'pie-safe',
    ],
  ),

  group(
    'small-buildings',
    'structure',
    'house',
    'upright',
    'a complete small building with one dominant roofline',
    ['wood', 'stone', 'plant'],
    'place',
    3,
    [
      'garden-cabin',
      'courtyard-house',
      'stilt-house',
      'earth-lodge',
      'roundhouse',
      'a-frame-hut',
      'lean-to-room',
      'sunroom',
      'sleeping-pod',
      'reading-hut',
      'tea-house',
      'gatehouse',
      'caretaker-cottage',
      'lake-cottage',
      'hill-shelter',
      'tool-shed',
      'seed-house',
      'potting-shed',
      'bath-hut',
      'quiet-room',
    ],
  ),
  group(
    'garden-structures',
    'structure',
    'canopy',
    'upright',
    'posts and overhead growth or shade structure',
    ['wood', 'stone', 'plant'],
    'place',
    3,
    [
      'shade-pavilion',
      'vine-pavilion',
      'rose-bower',
      'orchard-arbor',
      'moon-gate',
      'sun-gate',
      'wisteria-walk',
      'covered-walk',
      'garden-colonnade',
      'terrace-canopy',
      'picnic-pavilion',
      'rain-shelter',
      'viewing-deck',
      'pond-deck',
      'tree-platform',
      'green-tunnel',
      'shade-sail',
      'nursery-frame',
      'winter-garden',
      'fern-house',
    ],
  ),
  group(
    'crossings',
    'structure',
    'bridge',
    'long-wide',
    'span, supports, and an obvious crossing path',
    ['wood', 'stone', 'plant'],
    'place',
    3,
    [
      'beam-bridge',
      'arch-bridge',
      'rope-bridge',
      'stepping-bridge',
      'covered-bridge',
      'boardwalk',
      'causeway',
      'ford-platform',
      'canal-crossing',
      'ditch-crossing',
      'switchback-stair',
      'garden-stair',
      'viewing-ramp',
      'jetty',
      'landing-stage',
    ],
  ),
  group(
    'community-places',
    'structure',
    'pavilion',
    'wide',
    'a social focal point with a clear gathering purpose',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    3,
    [
      'meeting-pavilion',
      'story-circle',
      'outdoor-classroom',
      'picnic-hall',
      'tea-pavilion',
      'music-pavilion',
      'dance-platform',
      'market-shelter',
      'repair-commons',
      'tool-library',
      'seed-exchange',
      'trail-kiosk',
      'welcome-gate',
      'rest-station',
      'observation-hide',
    ],
  ),
  group(
    'work-structures',
    'structure',
    'workshop',
    'wide',
    'large working opening plus a specific process',
    ['wood', 'stone', 'found'],
    'place',
    3,
    [
      'carpentry-shop',
      'pottery-shed',
      'weaving-house',
      'repair-bay',
      'wash-house',
      'drying-house',
      'smoke-free-kiln',
      'watermill',
      'windmill-house',
      'pump-house',
      'boathouse',
      'storage-barn',
      'granary',
      'compost-house',
      'glasshouse',
    ],
  ),
  group(
    'landmarks',
    'structure',
    'tower',
    'tall',
    'a singular skyline shape and destination function',
    ['wood', 'stone', 'found'],
    'place',
    3,
    [
      'clocktower',
      'bell-tower',
      'lookout-tower',
      'water-tower',
      'weather-station',
      'observatory',
      'signal-mast',
      'wind-sculpture',
      'memory-monument',
      'wayfinding-spire',
    ],
  ),

  group(
    'datou-rest',
    'datou',
    'pet-rest',
    'low-wide',
    'robot-scale support for a specific resting pose',
    ['wood', 'stone', 'plant'],
    'place',
    1,
    [
      'curl-bed',
      'chin-rest',
      'cooling-pad',
      'warming-mat',
      'window-nook',
      'charging-nest',
      'rain-nook',
      'sun-patch-bed',
      'travel-bed',
      'quiet-cradle',
    ],
  ),
  group(
    'datou-play',
    'datou',
    'pet-play',
    'compact',
    'a clear chase, nudge, balance, or puzzle action',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'treat-puzzle',
      'scent-box',
      'nose-target',
      'paw-button',
      'rolling-drum',
      'wobble-board',
      'balance-beam',
      'weave-poles',
      'jump-hoop',
      'crawl-tube',
      'fetch-launcher',
      'tug-post',
      'spin-wheel',
      'bell-target',
      'hide-and-seek-box',
    ],
  ),
  group(
    'datou-care',
    'datou',
    'pet-care',
    'upright',
    'care access sized for Datou’s body and arm',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'grooming-stand',
      'paw-wash',
      'drying-station',
      'inspection-ramp',
      'brush-rack',
      'towel-warmer',
      'water-dispenser',
      'snack-drawer',
      'gear-stand',
      'visor-cleaner',
    ],
  ),
  group(
    'datou-explore',
    'datou',
    'pet-course',
    'long-wide',
    'a route choice or sensing challenge',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'scent-gate',
      'texture-path',
      'sound-post',
      'light-marker',
      'trail-beacon',
      'sniffing-wall',
      'lookout-step',
      'choice-gate',
      'memory-route',
      'discovery-dock',
    ],
  ),

  group(
    'memory-keepsakes',
    'keepsake',
    'memory',
    'upright',
    'one protected focal memory with a specific display mechanism',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'memory-album',
      'shadow-box',
      'memory-diorama',
      'postcard-carousel',
      'photo-cube',
      'story-scroll',
      'route-map',
      'pawprint-plaque',
      'date-marker',
      'memory-jar',
      'ticket-frame',
      'pressed-leaf-book',
      'milestone-board',
      'shared-journal',
      'memory-lantern',
    ],
  ),
  group(
    'sound-keepsakes',
    'keepsake',
    'sound',
    'compact',
    'visible parts that explain a gentle sound',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'music-box',
      'thumb-piano',
      'rain-stick',
      'tone-stones',
      'bell-tree',
      'whistle-post',
      'reed-organ',
      'drum-box',
      'echo-tube',
      'song-wheel',
    ],
  ),
  group(
    'display-keepsakes',
    'keepsake',
    'display',
    'upright',
    'a singular object presented with deliberate negative space',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'specimen-dome',
      'shell-stand',
      'feather-stand',
      'stone-pedestal',
      'flower-press',
      'curio-cabinet',
      'miniature-stage',
      'trophy-shelf',
      'discovery-mobile',
      'keepsake-tree',
    ],
  ),
  group(
    'seasonal-keepsakes',
    'keepsake',
    'seasonal',
    'compact',
    'seasonal motion or ritual without relying on color alone',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'spring-crown',
      'summer-fan',
      'autumn-wheel',
      'winter-star',
      'rain-day-marker',
      'fog-bell',
      'sun-disc',
      'moon-calendar',
      'first-snow-globe',
      'harvest-wreath',
    ],
  ),

  group(
    'garden-tools',
    'tool',
    'hand-tool',
    'long-thin',
    'working head and hand grip clearly communicate the task',
    ['wood', 'stone', 'found'],
    'place',
    2,
    [
      'hand-fork',
      'dibber',
      'soil-rake',
      'leaf-rake',
      'weeding-hook',
      'bulb-planter',
      'watering-wand',
      'pruning-knife',
      'garden-sieve',
      'seed-scoop',
    ],
  ),
  group(
    'craft-tools',
    'tool',
    'bench-tool',
    'compact',
    'a stable working face, guide, or clamp',
    ['wood', 'stone', 'found'],
    'place',
    2,
    [
      'mallet',
      'hand-saw',
      'brace-drill',
      'clamp',
      'wood-plane',
      'chisel',
      'awl',
      'spokeshave',
      'measuring-square',
      'marking-gauge',
    ],
  ),
  group(
    'observation-tools',
    'tool',
    'instrument',
    'compact',
    'lens, dial, pointer, or sample chamber explains observation',
    ['wood', 'stone', 'plant', 'found'],
    'place',
    2,
    [
      'magnifier',
      'periscope',
      'viewing-scope',
      'weather-vane',
      'rain-gauge',
      'wind-meter',
      'sun-dial',
      'soil-tester',
      'sound-funnel',
      'specimen-viewer',
    ],
  ),

  // --- Expansion: 492 additional forms (ITEM_DESIGN_EXPANSION_500) ----------
  group(
    'x500-kitchen',
    'furnishing',
    'kitchen',
    'upright',
    'a cooking or food-prep vessel and its working part',
    ['stone'],
    'place',
    2,
    [
      'tea-leaf-rinser',
      'pour-over-stand',
      'herb-steeping-basket',
      'dew-collector-cup',
      'spice-steam-pot',
      'warm-milk-pan',
      'steam-chime-kettle',
      'vegetable-washing-cradle',
      'root-scrubbing-board',
      'herb-chopping-trough',
      'dumpling-folding-tray',
      'rice-rinsing-basket',
      'soup-tasting-stand',
      'broth-cooling-rack',
      'bread-proofing-basket',
      'seed-roasting-pan',
      'meal-cover-cloche',
      'crumb-catching-board',
      'utensil-rinse-pot',
      'lunch-wrapping-press',
      'cooling-window-shelf',
      'portion-scoop-board',
      'snow-melt-tray',
      'heatwave-water-crock',
      'night-tea-warmer',
      'glow-paint-mixing-stone',
      'glue-warming-cup',
      'ceramic-repair-cradle',
      'shared-task-token-bowl',
      'festival-tea-urn',
      'neighbor-recipe-pot',
    ],
  ),
  group(
    'x500-light',
    'furnishing',
    'light',
    'tall',
    'a contained warm light source',
    ['found', 'stone'],
    'place',
    2,
    [
      'cup-warming-tile',
      'charcoal-tea-brazier',
      'tabletop-pot-warmer',
      'reading-light-hood',
      'storm-candle-box',
      'fog-path-beacon',
      'ice-lantern-mold',
      'bedside-glow-pebble',
      'constellation-pin-lantern',
      'path-glow-marker',
      'candle-reflector-fan',
      'sleep-timer-lantern',
      'porch-moth-light',
      'mirror-light-shelf',
      'window-candle-bridge',
      'amber-step-light',
      'night-bloom-spotlight',
      'soft-alarm-orb',
      'safe-return-lantern',
      'night-insect-sheet',
      'leaf-vein-lightbox',
      'welcome-light-chain',
      'story-listening-lantern',
      'insect-night-shield',
      'threshold-night-strip',
      'lantern-floating-basin',
      'winter-light-window',
      'quiet-hour-light',
      'goodbye-lantern-path',
    ],
  ),
  group(
    'x500-storage',
    'furnishing',
    'storage',
    'wide',
    'an enclosing box, drawer, or bin and its opening',
    ['wood', 'found'],
    'place',
    2,
    [
      'travel-tea-caddy',
      'picnic-brew-box',
      'cupboard-tea-drawer',
      'compost-scrap-caddy',
      'lap-blanket-basket',
      'cushion-stack-cart',
      'note-sorting-tree',
      'paper-scrap-comb',
      'bulb-storage-hammock',
      'community-seed-dispenser',
      'season-token-drawer',
      'moon-phase-nesting-boxes',
      'trail-snack-cache',
      'trail-register-box',
      'trail-repair-satchel',
      'driftwood-sorting-cradle',
      'moss-moisture-box',
      'wood-steaming-box',
      'salvage-washing-basket',
      'maker-cleanup-cart',
      'trail-gear-cubby',
      'under-bench-slidebox',
      'stack-lock-crates',
      'season-rotation-cabinet',
      'open-front-seed-wall',
      'rolled-textile-hive',
      'wet-dry-split-basket',
      'tiny-drawer-stair',
      'found-parts-library',
      'fragile-item-nest',
      'airing-storage-bench',
      'daily-use-carousel',
      'memory-scent-box',
      'kindness-repair-basket',
      'neighbor-gift-hatch',
      'lost-found-story-shelf',
      'bee-hotel-cleaning-tray',
      'feather-drop-basket',
      'habitat-care-cart',
      'porch-package-drawer',
      'entry-gear-turntable',
      'guest-slipper-drawer',
      'bench-leveling-wedgebox',
      'community-tool-locker',
      'repair-material-cache',
    ],
  ),
  group(
    'x500-utility',
    'furnishing',
    'utility',
    'upright',
    'a functional household stand or holder',
    ['wood', 'found'],
    'place',
    2,
    [
      'kettle-nest',
      'shared-sip-tray',
      'honey-drip-stand',
      'tea-scoop-rest',
      'pan-lid-tree',
      'blanket-ladder',
      'rain-cloak-dryer',
      'canopy-tieback-tree',
      'bedside-slipper-cradle',
      'pencil-shaving-cup',
      'stamp-carving-block',
      'page-turning-weight',
      'grafting-tool-rest',
      'seedling-carry-tray',
      'garden-tool-wash',
      'garden-kneeling-sled',
      'mud-boot-scraper',
      'sun-hat-shaping-block',
      'weather-cloak-stand',
      'lampwick-trimming-dock',
      'toy-repair-bench',
      'walking-stick-dock',
      'slope-rest-rail',
      'trail-water-rest',
      'shoreline-sample-tray',
      'shore-towel-tree',
      'shoreline-litter-sieve',
      'shore-picnic-anchor',
      'sunset-thermos-dock',
      'water-wheel-test-bench',
      'curved-part-vise',
      'small-parts-catchmat',
      'paint-straining-stand',
      'quiet-hammer-block',
      'repair-clamp-tree',
      'hanging-pocket-tree',
      'jar-carry-frame',
      'mudroom-drop-rail',
      'pond-escape-ladder',
      'welcome-boot-tray',
      'entry-scent-cloth-hook',
      'door-draft-snake-cradle',
      'parcel-carry-sled',
      'lamp-cleaning-perch',
      'litter-sort-sled',
      'moon-viewing-tray',
      'first-rain-cup-tree',
    ],
  ),
  group(
    'x500-instrument',
    'furnishing',
    'instrument',
    'compact',
    'a measuring or observation device and its readout',
    ['wood', 'found'],
    'place',
    2,
    [
      'tea-timer-float',
      'soil-moisture-flower',
      'harvest-weigh-balance',
      'frost-warning-flower',
      'snow-depth-post',
      'cloud-shadow-dial',
      'night-compass-disc',
      'starlight-photo-rest',
      'wind-tempo-spinner',
      'coolant-check-window',
      'route-readiness-board',
      'pack-balance-frame',
      'bridge-depth-gauge',
      'rest-stop-sundial',
      'pond-viewing-scope',
      'water-clarity-disc',
      'rain-barrel-level-float',
      'ripple-measuring-ring',
      'puddle-depth-flower',
      'feather-balance-stand',
      'soil-layer-column',
      'dew-magnifier-stand',
      'pinecone-weather-gauge',
      'moon-angle-quadrant',
      'compost-heat-spire',
      'fastener-length-gauge',
      'tool-edge-inspector',
      'spring-tension-tester',
      'memory-weather-vane',
      'erosion-check-stair',
    ],
  ),
  group(
    'x500-textile',
    'furnishing',
    'textile',
    'flat',
    'a soft padded or woven surface',
    ['plant'],
    'place',
    2,
    [
      'warming-flask-sleeve',
      'floor-reading-nest',
      'boot-warming-mat',
      'backrest-roll',
      'winter-foot-pocket',
      'porch-foot-hammock',
      'warm-stone-pocket',
      'soft-step-runner',
      'star-viewing-hood',
      'pond-edge-kneeler',
      'community-route-quilt',
      'doorway-height-ribbons',
    ],
  ),
  group(
    'x500-mechanism',
    'furnishing',
    'mechanism',
    'compact',
    'a turning wheel, dial, or drum and its working motion',
    ['wood', 'found'],
    'place',
    2,
    [
      'tea-brick-press',
      'hand-mill-table',
      'pickle-weight-press',
      'fruit-slicing-guide',
      'spice-drawer-wheel',
      'ink-drying-carousel',
      'paper-flower-cutter',
      'book-repair-press',
      'story-choice-spinner',
      'seed-spacing-wheel',
      'compost-turning-drum',
      'garden-row-marker',
      'vine-training-wheel',
      'mulch-chopping-box',
      'herb-snipping-carousel',
      'umbrella-drying-wheel',
      'sunrise-curtain-clock',
      'shadow-story-wheel',
      'glow-thread-spool',
      'breadcrumb-stone-setter',
      'trail-ribbon-reel',
      'walking-pace-wheel',
      'shell-rinsing-wheel',
      'lake-breeze-dryer',
      'boat-towing-reel',
      'sound-direction-ring',
      'magnet-find-tray',
      'wind-load-model',
      'rain-drop-counter',
      'neighbor-message-wheel',
      'community-tea-wheel',
      'squirrel-feeding-puzzle',
      'wildlife-water-leveler',
      'porch-key-stone',
      'doorstep-rinse-pump',
      'goodnight-porch-switch',
      'path-gravel-spreader',
      'trail-count-clicker',
      'trail-paint-stencil-roll',
      'park-care-route-cart',
      'harvest-sharing-wheel',
      'picnic-cloth-wheel',
      'season-turning-wheel',
    ],
  ),
  group(
    'x500-garden',
    'furnishing',
    'garden',
    'upright',
    'a growing vessel or plant-care structure',
    ['stone'],
    'place',
    2,
    [
      'cup-rinsing-fountain',
      'seed-paper-basin',
      'root-viewing-planter',
      'self-watering-spike',
      'sprout-protection-ring',
      'rain-chain-planter',
      'pollinator-drinking-stone',
      'mushroom-shade-log',
      'winter-cloche-vent',
      'autumn-leaf-catcher',
      'windbreak-planter-wall',
      'firefly-viewing-box',
      'water-plant-anchor',
      'shared-wish-planter',
      'friendship-plant-exchange',
      'bee-water-pebble-tray',
      'birdbath-ramp-insert',
      'wildlife-track-sandbed',
      'caterpillar-plant-guard',
      'threshold-flower-pocket',
      'tree-guard-expander',
      'kindness-token-fountain',
    ],
  ),
  group(
    'x500-frame',
    'furnishing',
    'frame',
    'upright',
    'open rectangular lattice and what it holds',
    ['wood', 'plant'],
    'place',
    2,
    [
      'tea-cloth-rack',
      'herb-drying-dome',
      'noodle-drying-frame',
      'pillow-airing-frame',
      'cushion-mending-hoop',
      'bookmark-weaving-frame',
      'postcard-drying-line',
      'nature-rubbing-board',
      'paper-lantern-folding-jig',
      'worm-casting-sieve',
      'fruit-catching-apron',
      'leaf-mold-cage',
      'pollen-window-screen',
      'bird-footprint-caster',
      'finish-drying-cage',
      'hedgehog-passage-gate',
      'pollinator-windbreak',
      'dragonfly-perch-ring',
      'culvert-viewing-hatch',
      'repair-day-apron-rack',
    ],
  ),
  group(
    'x500-conduit',
    'furnishing',
    'conduit',
    'long-thin',
    'a channel or pipe directing flow',
    ['stone'],
    'place',
    2,
    [
      'tea-water-filter',
      'rain-sound-gutter',
      'floating-leaf-race',
      'rainwater-first-flush',
      'seed-flight-tunnel',
      'echo-distance-tubes',
      'bat-water-trough',
      'burrow-rain-diverter',
      'rain-garden-diverter',
      'public-water-flush',
    ],
  ),
  group(
    'x500-seat-wide',
    'furnishing',
    'seat-wide',
    'low-wide',
    'a wide bench seat and its supports',
    ['wood'],
    'place',
    2,
    [
      'tea-sharing-stool',
      'sun-warmed-lounger',
      'quiet-company-benchlet',
      'visitor-choice-bench',
    ],
  ),
  group(
    'x500-display',
    'furnishing',
    'display',
    'upright',
    'a stand or frame that presents something',
    ['wood', 'found'],
    'place',
    2,
    [
      'pantry-inventory-rail',
      'field-journal-cradle',
      'route-thread-map',
      'wind-direction-ribbons',
      'seasonal-door-swag-frame',
      'dusk-window-prism',
      'route-choice-post',
      'path-condition-board',
      'switchback-model',
      'waypoint-kite',
      'map-weather-cover',
      'shortcut-gate-token',
      'ice-edge-warning-float',
      'water-reflection-frame',
      'insect-viewing-stage',
      'shadow-length-board',
      'cloud-shape-frame',
      'tree-ring-viewer',
      'animal-path-camera-rest',
      'flower-opening-clock',
      'shared-hypothesis-board',
      'patch-cutting-template',
      'repair-sequence-board',
      'specimen-shadow-shelf',
      'tool-outline-panel',
      'foldaway-display-ladder',
      'corner-swing-shelf',
      'basket-label-charms',
      'shared-calendar-tree',
      'story-object-stage',
      'help-request-flapboard',
      'shared-recipe-board',
      'thank-you-stamp-tree',
      'beetle-log-window',
      'nest-distance-marker',
      'porch-note-slate',
      'entry-mirror-hood',
      'volunteer-checkout-board',
      'spring-ribbon-tree',
      'autumn-seed-mosaic',
      'shared-song-banner',
      'blossom-counting-garland',
      'shared-photo-frame',
    ],
  ),
  group(
    'x500-pet-rest',
    'datou',
    'pet-rest',
    'low-wide',
    'a low resting place sized for Datou',
    ['plant', 'wood'],
    'place',
    2,
    [
      'window-nap-sling',
      'weighted-comfort-pad',
      'cooling-reed-mat',
      'rest-bell-pillow',
      'travel-bed-roll',
      'cool-down-fan-nest',
      'goodnight-cover-cradle',
      'victory-rest-station',
    ],
  ),
  group(
    'x500-panel',
    'furnishing',
    'panel',
    'flat',
    'a flat upright board or screen face',
    ['wood', 'plant'],
    'place',
    2,
    [
      'quiet-corner-screen',
      'map-folding-board',
      'folding-letter-screen',
      'breeze-cooling-screen',
      'minnow-shadow-screen',
      'moth-rest-bark-board',
      'seed-sharing-fence',
      'porch-wind-pocket',
      'summer-shadow-theater',
    ],
  ),
  group(
    'x500-canopy',
    'structure',
    'canopy',
    'wide',
    'an overhead cover and its supports',
    ['wood', 'plant'],
    'place',
    3,
    [
      'folding-nap-canopy',
      'shade-cloth-sail',
      'hail-protection-fan',
      'sanding-dust-cowl',
      'winter-seed-snow-roof',
      'rainy-arrival-canopy',
      'porch-sun-screen',
      'trail-story-pavilion-kit',
    ],
  ),
  group(
    'x500-memory',
    'keepsake',
    'memory',
    'compact',
    'a keepsake or archival object and its viewing detail',
    ['wood', 'found'],
    'place',
    2,
    [
      'headrest-drawer',
      'story-quilt-frame',
      'leaf-press-book',
      'story-scroll-box',
      'memory-caption-box',
      'weather-note-capsule',
      'dream-note-lantern',
      'distance-counting-beads',
      'route-memory-wheel',
      'rotating-keepsake-drawer',
      'memory-object-plinth',
      'archive-card-cabinet',
      'walk-token-press',
      'memory-thread-loom',
      'anniversary-shadow-box',
      'farewell-keepsake-drawer',
      'threshold-memory-tile',
      'homecoming-token-rail',
      'memory-parade-cart',
      'year-end-memory-scale',
    ],
  ),
  group(
    'x500-table',
    'furnishing',
    'table',
    'low-wide',
    'a flat work surface on legs',
    ['wood'],
    'place',
    2,
    [
      'portable-writing-knee',
      'dry-fit-assembly-table',
      'community-mending-circle',
      'porch-tea-hatch',
      'welcome-table-extension',
    ],
  ),
  group(
    'x500-slab',
    'furnishing',
    'slab',
    'flat',
    'flat low cut-stone or board mass',
    ['stone'],
    'place',
    1,
    [
      'charcoal-sketch-rest',
      'reed-boat-dock',
      'pond-skater-platform',
      'temperature-color-stones',
      'butterfly-sun-shelf',
      'frog-shade-tile',
      'snail-crossing-bridge',
      'root-protection-bridgelet',
      'path-puddle-bypass',
    ],
  ),
  group(
    'x500-sound',
    'furnishing',
    'sound',
    'compact',
    'a resonating or listening object',
    ['wood', 'plant'],
    'place',
    2,
    [
      'thunder-comfort-drum',
      'rainstick-bench',
      'pebble-tone-board',
      'reed-humming-wheel',
      'echo-listening-cone',
      'footstep-drum-mat',
      'birdcall-whistle-tree',
      'water-drop-metronome',
      'string-plucking-post',
      'shell-listening-rack',
      'story-sound-box',
      'breeze-bass-harp',
      'acorn-rhythm-tray',
      'bell-muffling-sleeve',
      'echo-marker-clapper',
      'leaf-rustle-frame',
      'hum-recording-horn',
      'pond-tone-float',
      'quiet-practice-pad',
      'duet-pipe-pair',
      'sound-map-table',
      'seed-pod-shaker-rack',
      'porch-cello-box',
      'listening-pillow',
      'goodnight-chord-lamp',
      'arrival-bell-stone',
      'water-sound-cup-chain',
      'shell-tone-basin',
      'returning-route-bell',
      'welcome-chime-shutter',
      'community-bell-rope',
      'new-walk-bell',
    ],
  ),
  group(
    'x500-pet-care',
    'datou',
    'pet-care',
    'upright',
    'a grooming or care station sized for Datou',
    ['wood', 'found'],
    'place',
    2,
    [
      'raincoat-button-board',
      'joint-warming-wrap',
      'visor-cleaning-dock',
      'shell-polishing-turntable',
      'charging-cable-swingarm',
      'maintenance-treat-drawer',
      'paw-pad-drying-rack',
      'quiet-checkup-mat',
      'battery-warmth-sleeve',
      'scratch-location-board',
      'rest-choice-carousel',
      'sensor-drying-cabinet',
      'paw-fastener-tray',
      'calm-breath-light',
      'comfort-scent-cloth',
      'shell-patch-press',
      'care-tool-roll',
      'morning-status-bell',
      'route-scent-capsule',
      'door-paw-bell',
    ],
  ),
  group(
    'x500-hand-tool',
    'tool',
    'hand-tool',
    'long-thin',
    'a hand-held tool head and handle',
    ['wood', 'found'],
    'tool',
    1,
    [
      'lantern-carry-yoke',
      'ear-sensor-brush',
      'brush-pressure-gauge',
      'dust-blower-bellows',
      'mud-depth-sandal',
      'fallen-branch-lifter',
      'brush-shaping-comb',
      'path-drain-rake',
      'stepping-stone-lifter',
      'trail-edge-stringline',
      'sign-cleaning-squeegee',
      'fence-gap-gauge',
      'bridge-board-tapper',
      'marker-post-driver',
      'boardwalk-grip-brush',
    ],
  ),
  group(
    'x500-pet-play',
    'datou',
    'pet-play',
    'compact',
    'a play apparatus sized for Datou',
    ['wood', 'found'],
    'place',
    2,
    [
      'rolling-treat-drum',
      'follow-light-puck',
      'scent-trail-stamper',
      'peekaboo-shutter-box',
      'tug-anchor-post',
      'rolling-bell-maze',
      'target-touch-tree',
      'fetch-sort-basket',
      'hide-cloth-frame',
      'bubble-chasing-bellows',
      'treat-count-abacus',
      'sound-follow-posts',
      'soft-block-builder',
      'rolling-leaf-wheel',
      'pause-and-go-lantern',
      'sniffing-flower-pots',
      'toy-return-chute',
      'shared-choice-shelf',
      'door-latch-practice-board',
    ],
  ),
  group(
    'x500-pet-course',
    'datou',
    'pet-course',
    'long-wide',
    'a low course Datou moves through',
    ['wood', 'plant'],
    'place',
    2,
    [
      'balance-wobble-bridge',
      'tunnel-window-run',
      'paw-paint-walkway',
      'gentle-jump-hoop',
      'treasure-cloth-map',
      'test-roll-ramp',
    ],
  ),
  group(
    'x500-bench-tool',
    'tool',
    'bench-tool',
    'compact',
    'a bench-mounted tool and its working part',
    ['wood', 'found'],
    'tool',
    2,
    [
      'cord-splicing-jig',
      'hinge-alignment-frame',
      'hand-drill-guide',
      'wheel-truing-fork',
      'thread-wax-dock',
    ],
  ),
] as const satisfies readonly IdentityGroup[];

export type CatalogFormId = (typeof GROUPS)[number]['ids'][number];

export const CATALOG_FORMS: Readonly<Record<CatalogFormId, Form>> = buildCatalog();

export const CATALOG_GROUPS = GROUPS;

function buildCatalog(): Record<CatalogFormId, Form> {
  const out: Partial<Record<CatalogFormId, Form>> = {};
  let ordinal = 0;
  for (const group of GROUPS) {
    for (const id of group.ids) {
      out[id as CatalogFormId] = {
        family: group.family,
        accepts: group.accepts,
        use: group.use,
        tier: group.tier,
        rarity: rarityFor(ordinal++, group.tier),
        sizes: group.silhouette !== 'joint' && group.silhouette !== 'binding',
        finishes: group.family !== 'component',
        identity: identityFor(id, group),
      };
    }
  }
  return out as Record<CatalogFormId, Form>;
}

function identityFor(id: string, group: IdentityGroup): FormIdentity {
  const name = id.replaceAll('-', ' ');
  const lastWord = name.split(' ').at(-1) ?? name;
  return {
    name,
    silhouette: group.silhouette,
    proportions: group.proportions,
    functionalCue: `${group.cue}; it must read immediately as a ${name}`,
    signatureFeatures: [
      `${name} primary geometry`,
      `${lastWord} specific working detail`,
      `one quiet asymmetrical feature unique to the ${name}`,
    ],
    duplicateGroup: group.key,
  };
}

function rarityFor(index: number, tier: Form['tier']): ItemRarity {
  if (index % 47 === 0) return 'legendary';
  if (index % 17 === 0) return 'epic';
  if (index % 7 === 0 || tier === 3) return 'rare';
  if (index % 3 === 0 || tier === 2) return 'uncommon';
  return 'common';
}

function group<const Ids extends readonly string[]>(
  key: string,
  family: FormFamily,
  silhouette: IdentitySilhouette,
  proportions: FormIdentity['proportions'],
  cue: string,
  accepts: Form['accepts'],
  use: FormUse,
  tier: Form['tier'],
  ids: Ids,
): IdentityGroup & { readonly ids: Ids } {
  return { key, family, silhouette, proportions, cue, accepts, use, tier, ids };
}
